import { DEFAULT_LOCALE, isLocale, SUPPORTED_LOCALES } from './locales.js';
/**
 * Picks the best supported locale from an `Accept-Language` header.
 *
 * Quality values are honoured, and a regional tag falls back to its base
 * language — a browser asking for `fr-CA,fr;q=0.9,en;q=0.8` gets French.
 */
export function negotiateLocale(acceptLanguage, fallback = DEFAULT_LOCALE) {
    if (!acceptLanguage)
        return fallback;
    const ranked = acceptLanguage
        .split(',')
        .map((part) => {
        const [tag, ...params] = part.trim().split(';');
        const q = params
            .map((param) => param.trim())
            .find((param) => param.startsWith('q='))
            ?.slice(2);
        return { tag: (tag ?? '').trim().toLowerCase(), quality: q ? Number(q) : 1 };
    })
        .filter((entry) => entry.tag && !Number.isNaN(entry.quality))
        .sort((a, b) => b.quality - a.quality);
    for (const { tag } of ranked) {
        if (tag === '*')
            return fallback;
        if (isLocale(tag))
            return tag;
        const base = tag.split('-')[0];
        if (isLocale(base))
            return base;
    }
    return fallback;
}
/**
 * Guesses a locale from a phone number's country code, used for the very
 * first WhatsApp message — before the customer has told us anything.
 * Rwanda leans Kinyarwanda, francophone West/Central Africa leans French.
 */
const DIALLING_CODE_LOCALES = {
    '250': 'rw', // Rwanda
    '257': 'fr', // Burundi
    '243': 'fr', // DR Congo
    '242': 'fr', // Congo-Brazzaville
    '225': 'fr', // Côte d'Ivoire
    '221': 'fr', // Senegal
    '223': 'fr', // Mali
    '226': 'fr', // Burkina Faso
    '229': 'fr', // Benin
    '228': 'fr', // Togo
    '241': 'fr', // Gabon
    '237': 'fr', // Cameroon
    '33': 'fr', // France
    '32': 'fr', // Belgium
};
/**
 * Words that identify which language someone is writing in.
 *
 * Not a vocabulary of each language — a marker earns its place only if a
 * speaker of the other two would not type it. "hi", "hello", "ok", "menu"
 * and "no" are how everybody opens a WhatsApp chat whatever they speak, so
 * they identify nothing and are left out on purpose: counting them would
 * answer a Kigali customer in English because they waved.
 *
 * Written without accents and at least three letters long, because that is
 * what `tokenise` produces. Short function words are avoided for the same
 * reason they are bad evidence: French "a" and "on" are English words too.
 *
 * A language's own name is a marker for itself ("francais", "kinyarwanda")
 * because both readings agree — whether the customer speaks it or is asking
 * for it, that is the language to answer in. A name in *another* language
 * ("anglais", "icyongereza") is deliberately absent, because there the two
 * readings disagree and `pickLanguage` in the bot owns that question.
 */
const TEXT_MARKERS = {
    en: [
        'car', 'cars', 'rent', 'rental', 'hire', 'hiring', 'need', 'want', 'book',
        'booking', 'available', 'availability', 'price', 'cost', 'much', 'day',
        'days', 'week', 'weekend', 'month', 'today', 'tomorrow', 'morning',
        'afternoon', 'evening', 'good', 'please', 'thanks', 'thank', 'looking',
        'from', 'with', 'have', 'would', 'like', 'your', 'this', 'what', 'when',
        'where', 'english',
    ],
    rw: [
        'muraho', 'mwaramutse', 'mwiriwe', 'bite', 'amakuru', 'murakoze', 'yego',
        'oya', 'imodoka', 'modoka', 'nshaka', 'ndashaka', 'nkeneye', 'gukodesha',
        'kode', 'ejo', 'ubu', 'iki', 'ibi', 'neza', 'cyane', 'kugeza', 'iminsi',
        'umunsi', 'icyumweru', 'ukwezi', 'nde', 'ryari', 'angahe', 'kinyarwanda',
        'ikinyarwanda',
    ],
    fr: [
        'bonjour', 'bonsoir', 'salut', 'coucou', 'merci', 'voiture', 'voitures',
        'louer', 'besoin', 'veux', 'voudrais', 'cherche', 'combien', 'prix',
        'disponible', 'reserver', 'jour', 'jours', 'semaine', 'mois', 'demain',
        'matin', 'soir', 'pour', 'avec', 'vous', 'nous', 'une', 'des', 'les',
        'est', 'que', 'qui', 'mon', 'moi', 'oui', 'non', 'svp', 'plait',
        'francais',
    ],
};
const MARKER_SETS = Object.entries(TEXT_MARKERS).map(([locale, words]) => [locale, new Set(words)]);
/** Only the opening of a message is evidence; the rest is the request. */
const MAX_SCANNED_CHARS = 400;
/**
 * Lower-cased, accent-stripped words of three letters or more.
 *
 * Folding the accents means the marker tables can be written plainly and
 * still match someone typing "français" or "reserver" either way — phones
 * make accents hard enough that both spellings turn up.
 */
function tokenise(text) {
    return text
        .slice(0, MAX_SCANNED_CHARS)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .split(/[^a-z]+/)
        .filter((word) => word.length >= 3);
}
/**
 * The language someone is writing in, or null when the text does not say.
 *
 * Null is the common answer and the honest one: "Hi", "ZF-Q0BJ2" and an
 * emoji carry no signal, and the caller has a better guess to fall back on
 * than a coin toss. A locale comes back only when it matches more markers
 * than either other language — a tie is no evidence at all.
 */
export function localeFromText(text) {
    if (!text)
        return null;
    // Distinct markers, so "voiture voiture voiture" counts once.
    const words = new Set(tokenise(text));
    if (words.size === 0)
        return null;
    let best = null;
    let bestScore = 0;
    let tied = false;
    for (const [locale, markers] of MARKER_SETS) {
        let score = 0;
        for (const word of words)
            if (markers.has(word))
                score += 1;
        if (score > bestScore) {
            best = locale;
            bestScore = score;
            tied = false;
        }
        else if (score === bestScore && score > 0) {
            tied = true;
        }
    }
    return tied ? null : best;
}
export function localeFromPhone(e164, fallback = DEFAULT_LOCALE) {
    const digits = e164.replace(/^\+/, '');
    // Longest prefix wins, so 250 is not shadowed by 25.
    for (const length of [3, 2, 1]) {
        const match = DIALLING_CODE_LOCALES[digits.slice(0, length)];
        if (match)
            return match;
    }
    return fallback;
}
export { SUPPORTED_LOCALES };
